import { useEffect, useReducer, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { parseApiError } from "@/utils/errorHandler";
import { configReducer, createInitialState } from "./simulationConfigurationReducer";
import { simulationService } from "@/api/simulationService";
import * as configService from "@/api/simulationConfigurationService";
import { uploadImage, deleteImage } from '@/api/firebaseService';
import type { question, test } from "./types.api";
import { validateTestConfig } from "./simulationConfiguration.schema";

const QUESTIONS_PER_PAGE = 15;

type ImageChange = {
    fileToUpload?: File;
    urlToDelete?: string;
};

export const useSimulationConfiguration = () => {
    const { simulationId } = useParams<{ simulationId: string }>();
    const [state, dispatch] = useReducer(configReducer, createInitialState());
    const[currentPage, setCurrentPage] = useState(1);
    const [pendingImageChanges, setPendingImageChanges] = useState<Record<number, ImageChange>>({});

    useEffect(() => {
        if (!simulationId) return;
        const fetchData = async () => {
            dispatch({ type: "FETCH_START" });
            try {
                const [simulation, thematicAreas, competences] = await Promise.all([
                    simulationService.getById(simulationId),
                    configService.getThematicAreas(),
                    configService.getCompetences(),
                ]);
                dispatch({
                    type: "SET_INITIAL_DATA",
                    payload: { simulation, thematicAreas, competences }
                });
            } catch (err) {
                dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
            }
        };
        fetchData();
    },[simulationId]);

    useEffect(() => {
        if (!simulationId || !state.selectedSessionId || !state.simulation) {
            dispatch({ type: "SET_ACTIVE_TEST", payload: null });
            return;
        }
        const currentSession = state.simulation.sessions?.find(s => s.id === state.selectedSessionId);
        if (!currentSession) return;

        let testIdToFetch = state.selectedTestId;
        if (!testIdToFetch || !currentSession.tests.some(t => t.id === testIdToFetch)) {
            testIdToFetch = currentSession.tests[0]?.id || null;
            dispatch({ type: "SELECT_TEST", payload: testIdToFetch });
            if (!testIdToFetch) return;
        }

        const fetchTestDetails = async () => {
            dispatch({ type: "FETCH_START" });
            try {
                const testDetails = await configService.getSimulationTestById(
                    simulationId,
                    state.selectedSessionId!,
                    testIdToFetch!
                );
                dispatch({ type: "SET_ACTIVE_TEST", payload: testDetails });
                setCurrentPage(1);
            } catch (err) {
                dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
            }
        };
        fetchTestDetails();
    },[simulationId, state.selectedSessionId, state.selectedTestId, state.simulation]);

    const paginatedQuestions = useMemo(() => {
        if (!state.activeTest) return[];
        const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
        return state.activeTest.questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);
    }, [state.activeTest, currentPage]);

    const handleSessionChange = useCallback((id: number | null) => {
        setPendingImageChanges({});
        dispatch({ type: "SELECT_SESSION", payload: id });
    },[]);

    const handleTestChange = useCallback((id: number | null) => {
        setPendingImageChanges({});
        dispatch({ type: "SELECT_TEST", payload: id });
    },[]);

    const handleSettingUpdate = useCallback(
        (field: keyof test, value: any) => dispatch({ type: "UPDATE_ACTIVE_TEST_SETTINGS", payload: { [field]: value } }),[]
    );

    const handleQuestionUpdate = useCallback((updatedQuestion: any) => {
        if (!state.activeTest) return;

        const absoluteIndex = state.activeTest.questions.findIndex(q => q.id === updatedQuestion.id);
        if (absoluteIndex === -1) return;

        const originalQuestion = state.activeTest.questions[absoluteIndex];

        if (updatedQuestion.imageFile instanceof File) {
            setPendingImageChanges(prev => ({
                ...prev,
                [originalQuestion.id]: {
                    fileToUpload: updatedQuestion.imageFile,
                    urlToDelete: originalQuestion.content.image_url
                }
            }));
            updatedQuestion.content.image_url = URL.createObjectURL(updatedQuestion.imageFile);
            delete updatedQuestion.imageFile; 
        } 
        else if (updatedQuestion.removeImageFlag) {
            setPendingImageChanges(prev => ({
                ...prev,
                [originalQuestion.id]: {
                    fileToUpload: undefined,
                    urlToDelete: originalQuestion.content.image_url
                }
            }));
            delete updatedQuestion.removeImageFlag; 
        }

        dispatch({
            type: "UPDATE_QUESTION_OBJECT",
            payload: { questionIndex: absoluteIndex, updatedQuestion }
        });
    }, [state.activeTest]);

    const handleSave = useCallback(async () => {
        if (!simulationId || !state.activeTest || !state.selectedSessionId) return;

        dispatch({ type: "SAVE_START" });

        const testPayload = JSON.parse(JSON.stringify(state.activeTest));

        testPayload.questions = testPayload.questions.map((q: any) => {
            const thematicAreaId = typeof q.thematic_area === 'object' && q.thematic_area !== null ? q.thematic_area.id : q.thematic_area;
            const competenceId = typeof q.competence === 'object' && q.competence !== null ? q.competence.id : q.competence;
            return {
                id: q.id,
                difficulty: q.difficulty,
                correct_answer: q.correct_answer,
                content: q.content,
                thematic_area_id: thematicAreaId,
                competence_id: competenceId,
            };
        });

        const errors = validateTestConfig(state.activeTest);
        if (Object.keys(errors).length > 0) {
            dispatch({ type: "SET_VALIDATION_ERRORS", payload: errors });
            alert("Por favor, complete todos los campos requeridos.");
            dispatch({ type: "SAVE_FAILURE", payload: parseApiError({ message: "Error de validación" }) });
            return;
        }

        try {
            for (const questionIdStr in pendingImageChanges) {
                const questionId = parseInt(questionIdStr);
                const change = pendingImageChanges[questionId];
                const questionIndex = testPayload.questions.findIndex((q: question) => q.id === questionId);
                if (questionIndex === -1) continue;

                if (change.urlToDelete && !change.urlToDelete.startsWith('blob:')) {
                    await deleteImage(change.urlToDelete);
                }
                
                if (change.fileToUpload instanceof File) {
                    const newImageUrl = await uploadImage(change.fileToUpload);
                    testPayload.questions[questionIndex].content.image_url = newImageUrl;
                } else if (!change.fileToUpload) {
                    testPayload.questions[questionIndex].content.image_url = null;
                }
            }

            const savedTest = await configService.saveSimulationTest(
                simulationId,
                state.selectedSessionId,
                testPayload.id,
                testPayload
            );

            dispatch({ type: "SAVE_SUCCESS", payload: savedTest });
            setPendingImageChanges({});
            alert("Configuración guardada exitosamente.");

        } catch (err) {
            dispatch({ type: "SAVE_FAILURE", payload: parseApiError(err) });
            alert("Error al guardar la configuración.");
        }
    },[simulationId, state.activeTest, state.selectedSessionId, pendingImageChanges]);

    return {
        state,
        currentPage,
        paginatedQuestions,
        questionsPerPage: QUESTIONS_PER_PAGE,
        handleSessionChange,
        handleTestChange,
        handleSettingUpdate,
        handleQuestionUpdate,
        handleSave,
        setCurrentPage,
    };
};